import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DEFAULT_PAGE_SIZE, fill, ITEM_TRANSACTION_FILTERS, itemTransactionsPath, t,
  type ColumnDef, type ItemTransactionFilter, type ItemTransactionRow, type ItemTransactionsAnswer,
  type PageSize, type SortState, type StatusResolver, viewerScopeKey,
} from '@bms2/shared';
import { SegmentedTab } from '../../components/controls';
import { LoadFailure, ShowMorePager } from '../../components/composites';
import { ListViewer } from '../../components/composites/ListViewer';
import { get, useApi } from './api';
import styles from './ItemTransactionsPanel.module.css';

/** The rows Show more has added under the first page, held for the one item and chip they were read for. */
interface MorePages {
  key: string;
  rows: ItemTransactionRow[];
}

/**
 * 🔒 The Item page's Transactions tile (T1254; decision 1940): every line that
 * names the Item, newest first, with one chip per side, a count line and twenty
 * rows a page. 🔨 It reads its own route, so the record page's one answer stays
 * the size it was; a cost withheld from this viewer arrives as null and reads
 * Withheld, never as a zero (decisions 0075, 0125).
 * 🔒 A row whose source row was read too nests under it (decision 8880): a Bill
 * under its Purchase Order, an Invoice under its Sales Order, a receipt under
 * the order line it received, the parent open with its chevron and count.
 */
export function ItemTransactionsPanel({ itemId, apiBaseUrl = '' }: { itemId: string; apiBaseUrl?: string }) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState | null>(SERVER_ORDER);
  const [pageSize, setPageSize] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [filter, setFilter] = useState<ItemTransactionFilter>('all');
  const fetched = useApi<ItemTransactionsAnswer>(itemTransactionsPath(itemId, filter, 0), apiBaseUrl);
  const [more, setMore] = useState<MorePages>({ key: '', rows: [] });
  const [busy, setBusy] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const key = `${itemId}:${filter}`;
  const answer = fetched.state.kind === 'ready' ? fetched.state.data : null;
  const rows = answer ? [...answer.rows, ...(more.key === key ? more.rows : [])] : [];

  const showMore = async () => {
    setBusy(true);
    const next = await get<ItemTransactionsAnswer>(itemTransactionsPath(itemId, filter, rows.length), apiBaseUrl);
    setBusy(false);
    setMoreFailed(!next.ok);
    if (next.ok) setMore({ key, rows: [...(more.key === key ? more.rows : []), ...next.data.rows] });
  };

  const choose = (value: string) => {
    setFilter(value as ItemTransactionFilter);
    setSort(SERVER_ORDER);
    setMoreFailed(false);
  };

  return (
    <div className={styles.panel} data-item-transactions={itemId}>
      <SegmentedTab
        name={`item-transactions-${itemId}`}
        legend={t('itemTransactions.legend')}
        segments={ITEM_TRANSACTION_FILTERS.map((value) => ({
          value,
          label: answer ? `${t(`itemTransactions.filter.${value}`)} ${answer.counts[value]}` : t(`itemTransactions.filter.${value}`),
        }))}
        value={filter}
        onChange={choose}
      />
      <LoadFailure state={fetched.state} onRetry={fetched.reload} />
      {answer ? (
        <>
          <p className={styles.count} data-transactions-count={answer.total}>
            {fill(t('itemTransactions.count'), { shown: String(rows.length), total: String(answer.total) })}
          </p>
          {rows.length === 0 ? (
            <p className={styles.empty}>{answer.counts.all === 0 ? t('itemTransactions.empty') : t('itemTransactions.emptyFilter')}</p>
          ) : (
            <ListViewer
              viewStore={{ scopeKey: viewerScopeKey('item-transactions'), apiBaseUrl, param: 'transactionsView' }}
              recordType={t('itemTransactions.legend')}
              views={ITEM_TRANSACTION_FILTERS.map((value) => ({ id: value, name: t(`itemTransactions.filter.${value}`) }))}
              currentViewId={filter}
              onViewChange={choose}
              columns={COLUMNS}
              rows={cellsOf(rows)}
              idOf={(cell) => cell.key}
              nest={{ parentOf: (cell) => cell.parentKey }}
              read={(cell, key) => cell[key as keyof TransactionCells]}
              sort={sort}
              onSortChange={setSort}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              status={{ resolve: statusOf(rows), entityTypeFor: () => STATUS_ENTITY }}
              emptyAction={t('itemTransactions.emptyFilter')}
              onRoute={(route) => navigate(route)}
              currencyCode={answer.currencyCode}
              selectable={false}
              bandless
            />
          )}
          <ShowMorePager visible={rows.length < answer.total} label={t('itemTransactions.showMore')} onShowMore={() => void showMore()} busy={busy} failed={moreFailed} failedText={t('itemTransactions.moreFailed')} />
        </>
      ) : null}
    </div>
  );
}

/**
 * 🔒 The tile's rows sit in the List Viewer like every other table (piece 4;
 * decision 1940): the view switcher and the chips both pick the side, the
 * viewer pages what Show More has read, and a header click re-sorts it.
 */
const COLUMNS: readonly ColumnDef[] = [
  { key: 'date', label: t('itemTransactions.column.date'), role: 'date', type: 'date' },
  { key: 'document', label: t('itemTransactions.column.document'), role: 'primary', type: 'link', linkPattern: '/:documentList/:documentId' },
  { key: 'account', label: t('itemTransactions.column.account'), role: 'secondary', type: 'link', linkPattern: '/:accountList/:accountId' },
  { key: 'quantity', label: t('itemTransactions.column.quantity'), role: 'secondary', type: 'text' },
  { key: 'unitPrice', label: t('itemTransactions.column.unitPrice'), role: 'secondary', type: 'currency' },
  { key: 'status', label: t('itemTransactions.column.status'), role: 'status', type: 'status', statusEntity: 'ItemTransaction' },
];

/** The enum name the status column resolves under; the pill's word and colour come from the row itself. */
const STATUS_ENTITY = 'ItemTransaction';

/** 🔒 Newest first, as the route answers (decision 1940): the position the row arrived in, until a header is clicked. */
const SERVER_ORDER: SortState = { key: 'position', direction: 'asc' };

/** One row as the List Viewer reads it: its cells, its place in the answer and the two addresses split for the link pattern. */
interface TransactionCells {
  key: string;
  /** The key of the row this one came from where it was read too (decision 8880); a row whose source is elsewhere stands alone. */
  parentKey: string | null;
  position: number;
  side: string;
  kind: string;
  date: string | null;
  document: string;
  documentList: string | null;
  documentId: string | null;
  account: string;
  accountList: string | null;
  accountId: string | null;
  quantity: string;
  /** Cents, or the word Withheld where this viewer is refused the cost (decisions 0075, 0125). */
  unitPrice: number | string | null;
  status: string | null;
}

/** The key a row is drawn under, its side and its id. */
function keyOf(row: ItemTransactionRow): string {
  return `${row.side}-${row.id}`;
}

/** 🔒 Every row read so far as cells, each naming the row it came from where that row was read too (decision 8880). */
function cellsOf(rows: readonly ItemTransactionRow[]): TransactionCells[] {
  const keys = new Map(rows.map((row) => [row.id, keyOf(row)] as const));
  return rows.map((row, position) => cells(row, position, row.sourceLineId ? keys.get(row.sourceLineId) ?? null : null));
}

function cells(row: ItemTransactionRow, position: number, parentKey: string | null): TransactionCells {
  const document = split(row.route);
  const account = split(row.account ? row.accountRoute : null);
  return {
    key: keyOf(row),
    parentKey,
    position,
    side: row.side,
    kind: row.kind,
    date: row.date ? `${row.date}T00:00:00.000Z` : t('itemTransactions.noDate'),
    document: documentText(row),
    documentList: document?.list ?? null,
    documentId: document?.id ?? null,
    account: row.account ?? '',
    accountList: account?.list ?? null,
    accountId: account?.id ?? null,
    quantity: `${row.quantity} ${row.unit}`,
    unitPrice: row.unitPriceCents === null ? t('itemTransactions.withheld') : row.unitPriceCents,
    status: row.status?.text ?? null,
  };
}

/** A record page's address as its list and its id, the shape every source's page has; any other address draws its text alone. */
function split(route: string | null): { list: string; id: string } | null {
  const parts = route?.split('/').filter(Boolean) ?? [];
  return parts.length === 2 ? { list: parts[0]!, id: decodeURIComponent(parts[1]!) } : null;
}

/** The pill's word and colour as the row sent them; the API resolves the document's status, so nothing here looks it up again. */
function statusOf(rows: readonly ItemTransactionRow[]): StatusResolver {
  const groups = new Map(rows.flatMap((row) => (row.status ? [[row.status.text, row.status.group] as const] : [])));
  return (_entity, value) => {
    const colorGroup = groups.get(value);
    return colorGroup ? { label: value, colorGroup } : null;
  };
}

/** The kind and the number together, the number left off where the document has none yet. */
function documentText(row: ItemTransactionRow): string {
  return row.number ? `${row.kind} ${row.number}` : row.kind;
}
